/**
 * 一个小动作的页面（/me/moves/<单号>，TEACH-PAGES-117）：按教学单的状态（adm1）显示对应的那一步，不看动作读口里段在不在（A-2 10-16）。
 * - 草稿：续写描述（改草稿不占次数）；参考照片（./TeachRefs，最多 3 张）；「整理一下」把描述和照片整理成教学说明（./TeachUnderstand，
 *   TEACH-UNDERSTAND-UI-117，adm1 TEACH-WIRE-117 的路由）；「不教这个了」先问一句再取消（还没发出，直接取消）。
 *   次数用完时存的是「想法」，说清收费开放以后再接着教（整理照样能做，确认页再说收费开放以后才能确认）。
 * - 等确认：教学确认页（./TeachConfirm）。
 * - 准备中、检查中、核对中：本次学习——三步进度只亮当前那一步，不写时间；核对中不亮任何一步；可以先离开去看 TA（全站主屏：在家回小屋、在外去地图），每 20 秒只读刷新，不重发。
 *   视觉照 r7k 06-learning-A（TEACH-PAGES-VISUAL-117，I 10-16 通过）：顶上是 TA 在小窝里的场景（./TeachHero，下面写「TA 在学新动作」），
 *   一张卡写动作名和实际走到哪一步；参考稿底部「学会了会告诉你」不照抄（117 没有学会提醒），照实说准备好以后在哪看得到。
 * - 准备好了、可以用了、先不用了、照新样子准备中：学习成果（./TeachResult）。
 * - 没准备好、已退回次数：照实说，说清不是 TA 学不会，回到列表；不给「再试一次」（方案 13、16 节：不鼓励反复下单）。
 * - 取消、隔离、删除，或者读不到（不是这家的、草稿不是你发起的，adm1 一律 404）：说这个小动作已经不在了。
 * 焦点（设计评审第一轮高 6）：从描述页存好过来时、确认成功换成本次学习时，焦点落在这一页的标题上。
 * 草稿续写存好：表单不重挂（焦点留在原处），状态那一句换成「改好了，已经存上。」，读屏会念（评审第二轮高 2）。
 */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { toApiError } from "@/shared/api/errors";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { ErrorState, Icon, LoadingState, Page, TopBar } from "@/shared/ui";
import { PetPortrait } from "@/features/pets/PetPortrait";
import { WorldGate } from "@/features/world_map/WorldGate";
import { HOME_TAB, usePrimaryTab } from "@/app/primaryTab";
import {
  BACK_TO_LIFE, BACK_TO_LIST, BACK_TO_MAP, DRAFT_EDIT, DRAFT_SAVED, DRAFT_UPDATED, DROP, DROP_ASK, DROP_YES, ENDED_REASSURE, GONE, IDEA_SAVED, LEARNING_NEXT, STAGES,
  TEACH_ENTRY_TITLE, TEACH_STILL_LEARNING, canTeachNew, headerTitle, stageIndex, stateCopy, teachGroup,
} from "./teachModel";
import type { TeachOrder } from "./teachTypes";
import { useCanCare, useEntitlement, useOrder, useTeachApi, useTeachOpen } from "./useTeach";
import { useTeachAction } from "./teachActions";
import { DescribeForm } from "./TeachDescribePage";
import { TeachConfirm } from "./TeachConfirm";
import { TeachResult } from "./TeachResult";
import { TeachAsk } from "./TeachAsk";
import { TeachButton } from "./TeachButton";
import { TEACH_CLOSED, TEACH_DEMO, TEACH_NOT_CARER } from "./TeachHomePage";
import { TeachHero } from "./TeachHero";
import { TeachRefs } from "./TeachRefs";
import { TeachUnderstand } from "./TeachUnderstand";
import "@/features/me/me.css";
import "./teach.css";

export interface OrderPet {
  petId: string;
  name: string;
  species: import("@/shared/contracts").PetSpecies;
  photoUrl: string | null;
}

export function TeachOrderPage() {
  return (
    <WorldGate>
      <Page bare className="ps-teach">
        <TopBar title={TEACH_ENTRY_TITLE} back="/me/moves" backLabel="返回 TA 的小动作" />
        <OrderBody />
      </Page>
    </WorldGate>
  );
}

function OrderBody() {
  const { orderId = null } = useParams();
  const api = useTeachApi();
  const open = useTeachOpen();
  const canCare = useCanCare();
  const household = useOptionalCurrentHousehold();
  const order = useOrder(orderId);
  if (!api) return <p className="ps-teach-quiet" role="status">{TEACH_DEMO}</p>;
  if (open === null || canCare === null || order.isPending) return <LoadingState lines={3} label="正在打开这个小动作…" />;
  if (!open) return <p className="ps-teach-quiet" role="status">{TEACH_CLOSED}</p>;
  if (!canCare) return <p className="ps-teach-quiet" role="status">{TEACH_NOT_CARER}</p>;
  if (order.isError) return toApiError(order.error).isNotFound ? <Gone /> : <ErrorState error={order.error} onRetry={() => void order.refetch()} />;
  const brief = household?.pets.find(({ pet }) => pet.pet_id === order.data.pet_id)?.pet ?? null;
  if (!brief) return <Gone />;
  const pet: OrderPet = { petId: brief.pet_id, name: brief.name, species: brief.species, photoUrl: brief.photo_url };
  return <OrderView key={order.data.order_id} order={order.data} pet={pet} />;
}

function OrderView({ order, pet }: { order: TeachOrder; pet: OrderPet }) {
  switch (teachGroup(order.state)) {
    case "confirm":
      return <TeachConfirm order={order} pet={pet} />;
    case "unfinished":
      return <DraftView order={order} pet={pet} />;
    case "in_progress":
      return <LearningView order={order} pet={pet} />;
    case "learned":
      return <TeachResult order={order} pet={pet} />;
    case "ended":
      return <EndedView order={order} pet={pet} />;
    default:
      return <Gone />;
  }
}

/** 这一单的标题卡：本宠头像、小动作的名字、状态标签（本次学习页不挂标签：那一页的三步和一句话已经说了）。 */
export function OrderHeader({ order, pet, tag = true }: { order: TeachOrder; pet: OrderPet; tag?: boolean }) {
  const copy = stateCopy(order.state);
  return (
    <section className="ps-teach-card ps-teach-who">
      <PetPortrait petId={pet.petId} name={pet.name} species={pet.species} photoUrl={pet.photoUrl} size={52} />
      <div className="ps-teach-who__text">
        <OrderTitle order={order} />
        {tag && copy ? <p className="ps-teach-who__tag" data-state={order.state}>{copy.tag}</p> : null}
      </div>
    </section>
  );
}

/** 这一单的名字（h2）：从描述页存好过来、确认成功换成本次学习时，焦点落在这里（location.state.teachFocus＝"title"） */
function OrderTitle({ order }: { order: TeachOrder }) {
  const location = useLocation();
  const title = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if ((location.state as { teachFocus?: string } | null)?.teachFocus !== "title") return;
    // 标题在顶栏后面时滚到顶栏下面（scroll-margin），看得见就不动
    title.current?.focus({ preventScroll: true });
    title.current?.scrollIntoView?.({ block: "nearest" });
  }, [location.state]);
  return <h2 className="ps-teach-who__title" ref={title} tabIndex={-1}>{headerTitle(order)}</h2>;
}

function DraftView({ order, pet }: { order: TeachOrder; pet: OrderPet }) {
  const navigate = useNavigate();
  const entitlement = useEntitlement();
  const idea = entitlement.data ? !canTeachNew(entitlement.data) : false;
  const [asking, setAsking] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dropButton = useRef<HTMLButtonElement>(null);
  const edit = useTeachAction<string>(order, "edit", (api, _key, text) => api.editDraft(order.order_id, { draft_text: text, refs: order.refs ?? [] }));
  const drop = useTeachAction<null>(order, "cancel", (api, key) => api.cancel(order.order_id, key), () => navigate("/me/moves", { replace: true }));
  return (
    <>
      <OrderHeader order={order} pet={pet} />
      <p className="ps-teach-note" role="status">{edit.isSuccess ? DRAFT_UPDATED : idea ? IDEA_SAVED : DRAFT_SAVED}</p>
      <DescribeForm initial={order.draft_text ?? ""} busy={edit.isPending} error={edit.error} submitLabel={DRAFT_EDIT} onSubmit={(text) => edit.mutate(text)} onDirtyChange={setDirty} />
      <TeachRefs order={order} />
      <TeachUnderstand order={order} dirty={dirty} />
      <div className="ps-teach-card ps-teach-quietactions">
        {asking ? (
          <TeachAsk label={DROP} text={DROP_ASK} yes={DROP_YES} busy={drop.isPending} onYes={() => drop.mutate(null)} onNo={() => setAsking(false)} returnTo={dropButton} />
        ) : (
          <TeachButton buttonRef={dropButton} variant="ghost" onClick={() => setAsking(true)}>{DROP}</TeachButton>
        )}
        {drop.error ? <p className="ps-teach-form__error" role="alert">{toApiError(drop.error).playerMessage}</p> : null}
      </div>
    </>
  );
}

const STAGE_SAY = { done: "已完成", now: "进行中", later: "还没到" } as const;

function LearningView({ order, pet }: { order: TeachOrder; pet: OrderPet }) {
  const copy = stateCopy(order.state);
  const at = stageIndex(order.state);
  const spec = order.confirmed_spec ?? order.spec ?? null;
  // 回去看看 TA：全站同一个主屏（TA 在家回小屋，在外面去地图；app/primaryTab），不在这里写路由
  const tab = usePrimaryTab();
  return (
    <>
      <TeachHero pet={pet} orders={[]} caption={TEACH_STILL_LEARNING} />
      <section className="ps-teach-card ps-teach-learning" aria-label="本次学习">
        <div className="ps-teach-learning__head">
          <Icon name="sprout" size={20} className="ps-teach-group__icon" />
          <OrderTitle order={order} />
        </div>
        {at >= 0 ? (
          <ol className="ps-teach-progress" aria-label="学习到哪一步了">
            {STAGES.map((stage, index) => {
              const where = index === at ? "now" : index < at ? "done" : "later";
              return (
                <li key={stage} className="ps-teach-progress__step" data-at={where} aria-current={where === "now" ? "step" : undefined}>
                  <span className="ps-teach-progress__dot" aria-hidden="true" />
                  <span className="ps-teach-progress__label">{stage}</span>
                  <span className="visually-hidden">（{STAGE_SAY[where]}）</span>
                </li>
              );
            })}
          </ol>
        ) : null}
        {copy ? <p className="ps-teach-lead" role="status">{copy.line}</p> : null}
        {spec ? <SpecSummary steps={spec.steps} /> : null}
        <p className="ps-teach-note ps-teach-learning__next">{LEARNING_NEXT}</p>
        <Link to={tab.to} className="ps-btn ps-btn--secondary ps-btn--block">{tab === HOME_TAB ? BACK_TO_LIFE : BACK_TO_MAP}</Link>
      </section>
    </>
  );
}

export function SpecSummary({ steps }: { steps: readonly string[] }) {
  return (
    <ol className="ps-teach-steps" aria-label="具体动作">
      {steps.map((step, index) => <li key={index}>{step}</li>)}
    </ol>
  );
}

function EndedView({ order, pet }: { order: TeachOrder; pet: OrderPet }) {
  const copy = stateCopy(order.state);
  return (
    <>
      <OrderHeader order={order} pet={pet} />
      <section className="ps-teach-card">
        {copy ? <p className="ps-teach-lead" role="status">{copy.line}</p> : null}
        <p className="ps-teach-note">{ENDED_REASSURE}</p>
        <Link to="/me/moves" className="ps-btn ps-btn--secondary ps-btn--block">{BACK_TO_LIST}</Link>
      </section>
    </>
  );
}

function Gone() {
  return (
    <section className="ps-teach-card">
      <p className="ps-teach-lead" role="status">{GONE}</p>
      <Link to="/me/moves" className="ps-btn ps-btn--secondary ps-btn--block">{BACK_TO_LIST}</Link>
    </section>
  );
}
